import React, { useState } from "react";
import { Btn, Badge, Field, Input, Select, Modal, InlineModal, Toast, useToast } from "../components/ui";
import { fmtDate, fmtMoney, fmtMeasure } from "../lib/formatters";
import { isCompactViewport, SYSTEM_LISTS_DEFAULTS, T} from "../lib/globals";
import { BovineHeadIcon, BovineRationBagIcon, GenericRationBagIcon, HorseIcon, SheepHeadIcon } from "../lib/icons";
import { useSysLists } from "../sync/SyncProvider";

export function createLotDetailModal(deps) {
  const { AGE_RANGES } = deps;

  function LotDetailModal({ lot, pastureMap, canEdit, onClose, onEdit, onAddMovement, onToggleTermination, onIndividualize, linkedAnimals, exitReasonOptions=[], entryReasonOptions=[], buyerOptions=[], onCreateBuyer, inline=false }) {
  const [mvForm, setMvForm] = useState(null); // {type, qty, weight, note, weighMode, individualWeights, reason, amount, nfNum, gta, buyerId, createBuyer}
  const [toast, show] = useToast();
  const ageRange = AGE_RANGES.find(a=>a.id===lot.ageRange);
// eslint-disable-next-line no-unused-vars
  const __mvTypes = ["entrada","saída","pesagem","transferência"];
  const sysLists = useSysLists();
  const weightRules = Array.isArray(sysLists?.BOVINE_WEIGHT_CLASS_RULES) && sysLists.BOVINE_WEIGHT_CLASS_RULES.length
    ? sysLists.BOVINE_WEIGHT_CLASS_RULES
    : SYSTEM_LISTS_DEFAULTS.BOVINE_WEIGHT_CLASS_RULES;
  const normalizedWeightRules = (weightRules||[]).map(rule=>(
    { label:String(rule?.label||""), minAt:Number(rule?.minAt ?? rule?.min ?? 0), maxAt:Number(rule?.maxAt ?? rule?.max ?? 999), color:String(rule?.color||T.green) }
  )).sort((a,b)=>a.minAt-b.minAt);
  const carcassYieldPct = Math.min(60, Math.max(45, Number(lot?.carcassYieldPct || 50)));
  const avgCarcassKg = Number(lot?.avgWeight || 0) * (carcassYieldPct / 100);
  const avgArrobas = avgCarcassKg / 15;
  const avgRule = normalizedWeightRules.find(rule=>avgArrobas >= rule.minAt && avgArrobas <= rule.maxAt) || normalizedWeightRules[normalizedWeightRules.length-1] || { color:T.green };
  const totalCarcassKg = avgCarcassKg * Number(lot?.quantity || 0);

  const content = (
    <>
      <Toast toast={toast}/>
      <div style={{display:"flex",flexDirection:"column",gap:0}}>

        {/* Header */}
        <div style={{background:`${T.yellow}18`,borderRadius:12,padding:"16px 18px",marginBottom:16,border:`1px solid ${T.yellow}33`}}>
          <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
            <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",minHeight:36}}>{ageRange?.icon || (lot.species === "Ovino" ? <SheepHeadIcon size={34} /> : lot.species === "Equino" ? <HorseIcon size={34} /> : <BovineHeadIcon size={34} />)}</span>
            <div style={{flex:1}}>
              <div style={{fontWeight:800,color:T.text,fontSize:18,marginBottom:6}}>{lot.name}</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <Badge color={T.yellow}>{lot.species}</Badge>
                {lot.breed&&<Badge color={T.muted}>{lot.breed}</Badge>}
                <Badge color={lot.sex==="Macho"?T.accent:lot.sex==="Fêmea"?T.red:T.muted}>
                  {lot.sex==="Macho"?"♂":lot.sex==="Fêmea"?"♀":"⚥"} {lot.sex}
                </Badge>
                {lot.purpose&&<Badge color={T.purple}>{lot.purpose}</Badge>}
                {ageRange&&<Badge color={T.accent}>{ageRange.label}</Badge>}
              </div>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
              {canEdit && onIndividualize && <Btn size="sm" variant="ghost" onClick={onIndividualize}>Individualizar animais</Btn>}
              {canEdit&&<Btn size="sm" variant="ghost" onClick={onEdit}>✏️ Editar</Btn>}
            </div>
          </div>
        </div>

        {/* Key metrics */}
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"repeat(3,minmax(0,1fr))":"repeat(3,1fr)",gap:10,marginBottom:16}}>
          <div style={{background:T.card2,borderRadius:10,padding:"12px 10px",textAlign:"center",minWidth:0}}>
            <div style={{fontSize:isCompactViewport()?24:28,fontWeight:800,color:T.yellow,lineHeight:1.1}}>{lot.quantity||0}</div>
            <div style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:.5,marginTop:4}}>Animais</div>
          </div>
          <div style={{background:T.card2,borderRadius:10,padding:"12px 10px",textAlign:"center",minWidth:0}}>
            <div style={{fontSize:isCompactViewport()?20:28,fontWeight:800,color:avgRule?.color||T.green,lineHeight:1.1,wordBreak:"break-word"}}>{fmtMeasure(avgArrobas,2)} @</div>
            <div style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:.5,marginTop:4}}>Peso médio</div>
            <div style={{fontSize:11,color:T.muted,marginTop:3}}>{fmtMeasure(avgCarcassKg,1)} kg carcaça · balança {fmtMeasure(Number(lot.avgWeight||0),1)} kg</div>
          </div>
          <div style={{background:T.card2,borderRadius:10,padding:"12px 10px",textAlign:"center",minWidth:0}}>
            <div style={{fontSize:isCompactViewport()?18:22,fontWeight:800,color:T.accent,lineHeight:1.15,wordBreak:"break-word"}}>{fmtMeasure(totalCarcassKg,0)} kg</div>
            <div style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:.5,marginTop:4}}>Kg total carcaça</div>
            <div style={{fontSize:11,color:T.muted,marginTop:3}}>{fmtMeasure(Number(lot.totalWeight||0),0)} kg balança</div>
          </div>
        </div>

        {lot.species === "Bovino" && canEdit && (
          <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 14px",marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
            <div>
              <div style={{fontSize:11,color:T.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:.8}}>🏁 Lote especial</div>
              <div style={{fontSize:13,color:T.text,fontWeight:700,marginTop:4}}>Animais em Terminação</div>
              <div style={{fontSize:12,color:T.muted,marginTop:2}}>Marque quando este lote estiver em fase de terminação, independentemente da faixa etária.</div>
            </div>
            <label style={{display:"inline-flex",alignItems:"center",gap:8,fontSize:13,color:T.text,fontWeight:700}}>
              <input type="checkbox" checked={!!lot.terminationLot} onChange={e=>onToggleTermination && onToggleTermination(e.target.checked)} />
              {lot.terminationLot ? "Ativo" : "Desativado"}
            </label>
          </div>
        )}

        {/* Location + cost center */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
          {lot.pastureId&&pastureMap[lot.pastureId]&&(
            <div style={{background:T.card2,borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:T.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:.8}}>📍 Localização</div>
              <div style={{color:T.green,fontWeight:600,fontSize:13,marginTop:4}}>{pastureMap[lot.pastureId]}</div>
            </div>
          )}
          <div style={{background:T.card2,borderRadius:8,padding:"10px 12px"}}>
            <div style={{fontSize:10,color:T.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:.8}}>📅 Formado em</div>
            <div style={{color:T.text,fontWeight:600,fontSize:13,marginTop:4}}>{fmtDate(lot.formedAt)}</div>
          </div>
        </div>

        {/* Feeding info */}
        {(lot.feedType||lot.supplements?.length>0)&&(
          <div style={{background:`${T.green}10`,border:`1px solid ${T.green}33`,borderRadius:10,padding:"12px 16px",marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:T.green,textTransform:"uppercase",letterSpacing:.8,marginBottom:10,display:"inline-flex",alignItems:"center",gap:6}}>{lot.species === "Ovino" ? <GenericRationBagIcon size={19} /> : <BovineRationBagIcon size={19} />} Alimentação</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:12}}>
              {lot.feedType&&(
                <div>
                  <div style={{fontSize:10,color:T.muted,marginBottom:2}}>Sistema</div>
                  <Badge color={T.green}>{lot.feedType}</Badge>
                </div>
              )}
              {lot.dailyFeedKg>0&&(
                <div>
                  <div style={{fontSize:10,color:T.muted,marginBottom:2}}>Consumo/animal/dia</div>
                  <span style={{fontWeight:700,color:T.text,fontSize:13}}>{lot.dailyFeedKg} kg</span>
                  {lot.quantity>0&&<span style={{fontSize:11,color:T.muted}}> · Total: {fmtMoney(lot.dailyFeedKg*lot.quantity)} kg/dia</span>}
                </div>
              )}
            </div>
            {(lot.supplements||[]).length>0&&(
              <div style={{marginTop:10}}>
                <div style={{fontSize:10,color:T.muted,marginBottom:6}}>💊 Suplementos</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {lot.supplements.map((sup,i)=>(
                    <div key={i} style={{background:T.card,borderRadius:8,padding:"4px 10px",fontSize:12}}>
                      <span style={{color:T.yellow,fontWeight:600}}>{sup.type}</span>
                      {sup.dailyKg>0&&<span style={{color:T.muted}}> · {sup.dailyKg} kg/dia</span>}
                      {sup.notes&&<span style={{color:T.muted}}> · {sup.notes}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {lot.feedNotes&&(
              <div style={{fontSize:12,color:T.muted,marginTop:8,fontStyle:"italic"}}>"{lot.feedNotes}"</div>
            )}
          </div>
        )}

        {/* Linked individual animals */}
        {linkedAnimals.length>0&&(
          <div style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>
              🔗 Animais Vinculados ({linkedAnimals.length})
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {linkedAnimals.map(a=>(
                <span key={a.id} style={{background:`${T.accent}18`,color:T.accent,borderRadius:8,padding:"3px 10px",fontSize:12,fontWeight:600}}>
                  {a.tag} · {a.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Movement buttons */}
        {canEdit&&(
          <div style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>⚡ Registrar Movimentação</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {[
                {type:"entrada",     icon:"➕", color:T.green,  label:"Entrada"},
                {type:"saída",       icon:"➖", color:T.red,    label:"Saída"},
                {type:"pesagem",     icon:"⚖️", color:T.yellow, label:"Pesagem"},
                {type:"transferência",icon:"🔄",color:T.accent, label:"Transferência"},
              ].map(mv=>(
                <Btn key={mv.type} size="sm" variant="ghost"
                  style={{borderColor:mv.color,color:mv.color}}
                  onClick={()=>setMvForm({type:mv.type,qty:"",weight:"",note:"",weighMode:"average",individualWeights:[""],carcassYieldPct:lot.carcassYieldPct||50,reason:mv.type==="saída"?(exitReasonOptions[0]||""):(entryReasonOptions[0]||""),amount:"",nfNum:"",gta:"",buyerId:"",sellerName:"",paymentMethod:"",justificationMissing:"",createBuyer:null})}>
                  {mv.icon} {mv.label}
                </Btn>
              ))}
            </div>

            {/* Movement mini-form */}
            {mvForm&&(
              <div style={{background:T.card2,borderRadius:10,padding:"12px 14px",marginTop:10,border:`1px solid ${T.border}`}}>
                <div style={{fontWeight:700,color:T.text,fontSize:13,marginBottom:10,textTransform:"capitalize"}}>
                  {mvForm.type==="entrada"?"➕ Entrada de Animais":
                   mvForm.type==="saída"?"➖ Saída de Animais":
                   mvForm.type==="pesagem"?"⚖️ Pesagem do Lote":"🔄 Transferência"}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                  {mvForm.type!=="pesagem"&&(
                    <Field label={`Quantidade (${mvForm.type==="entrada"?"animais entrando":"animais saindo"})`}>
                      <Input type="number" min="1" value={mvForm.qty} placeholder="Ex: 10"
                        onChange={e=>setMvForm(f=>({...f,qty:e.target.value}))}/>
                    </Field>
                  )}
                  {(mvForm.type==="pesagem"||mvForm.type==="entrada")&&(
                    <>
                      {mvForm.type==="pesagem" && <Field label="Modo de pesagem" col="1/-1"><div style={{display:"flex",gap:8,flexWrap:"wrap"}}>{[{id:"average",label:"Lançar média"},{id:"individual",label:"Lançar pesos individuais"}].map(opt=><Btn key={opt.id} size="sm" variant="ghost" style={{borderColor:mvForm.weighMode===opt.id?T.yellow:T.border,color:mvForm.weighMode===opt.id?T.yellow:T.text}} onClick={()=>setMvForm(f=>({...f,weighMode:opt.id, individualWeights:Array.isArray(f.individualWeights)&&f.individualWeights.length?f.individualWeights:[""]}))}>{opt.label}</Btn>)}</div></Field>}
                      {(mvForm.type!=="pesagem" || mvForm.weighMode!=="individual") && <Field label={mvForm.type==="pesagem"?"Peso da balança médio (kg)":"Peso médio dos novos animais (kg)"}>
                        <Input type="number" min="0" step="0.1" value={mvForm.weight} placeholder="Ex: 380"
                          onChange={e=>setMvForm(f=>({...f,weight:e.target.value}))}/>
                      </Field>}
                      {mvForm.type==="pesagem" && <Field label="Aproveitamento de carcaça (%)">
                        <Input type="number" min="45" max="60" step="0.1" value={mvForm.carcassYieldPct||lot.carcassYieldPct||50} placeholder="Ex: 50" onChange={e=>setMvForm(f=>({...f,carcassYieldPct:e.target.value}))}/>
                      </Field>}
                      {mvForm.type==="pesagem" && mvForm.weighMode==="individual" && <Field label="Pesos individuais da balança (kg)" col="1/-1"><div style={{display:"grid",gap:8}}>{(mvForm.individualWeights||[""]).map((value,idx)=><div key={idx} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8}}><Input type="number" min="0" step="0.1" value={value} placeholder={`Animal ${idx+1} · peso em kg`} onChange={e=>setMvForm(f=>{ const list=[...(f.individualWeights||[])]; list[idx]=e.target.value; return {...f,individualWeights:list}; })}/><Btn size="sm" variant="danger" onClick={()=>setMvForm(f=>{ const list=(f.individualWeights||[]).filter((_,i)=>i!==idx); return {...f,individualWeights:list.length?list:[""]}; })}>Remover</Btn></div>)}<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap"}}><Btn size="sm" variant="ghost" onClick={()=>setMvForm(f=>({...f,individualWeights:[...(f.individualWeights||[]), ""]}))}>+ Adicionar peso</Btn><div style={{fontSize:12,color:T.muted}}>O app calcula a média automaticamente sem precisar identificar cada animal.</div></div></div></Field>}
                    </>
                  )}
                  {(mvForm.type==="saída" || mvForm.type==="entrada") && <Field label={mvForm.type==="saída"?"Motivo da saída *":"Motivo da entrada *"} col="1/-1">
                    <Select value={mvForm.reason||""} onChange={e=>setMvForm(f=>({...f,reason:e.target.value}))}>
                      {(mvForm.type==="saída" ? exitReasonOptions : entryReasonOptions).map(reason=><option key={reason} value={reason}>{reason}</option>)}
                    </Select>
                  </Field>}
                  {mvForm.type==="saída" && mvForm.reason==="Venda externa" && <>
                    <Field label="Comprador">
                      <Select value={mvForm.buyerId||""} onChange={e=>setMvForm(f=>({...f,buyerId:e.target.value}))}>
                        <option value="">Selecione</option>
                        {(buyerOptions||[]).map(b=><option key={b.id||b.name} value={b.id||""}>{b.name}{b.cpfCnpj?` · ${b.cpfCnpj}`:""}</option>)}
                      </Select>
                    </Field>
                    <Field label="Valor da venda (R$)"><Input type="number" min="0" step="0.01" value={mvForm.amount||""} onChange={e=>setMvForm(f=>({...f,amount:e.target.value}))}/></Field>
                    <Field label="GTA"><Input value={mvForm.gta||""} onChange={e=>setMvForm(f=>({...f,gta:e.target.value}))}/></Field>
                    <Field label="Nota fiscal"><Input value={mvForm.nfNum||""} onChange={e=>setMvForm(f=>({...f,nfNum:e.target.value}))}/></Field>
                    <Field label="Forma de pagamento"><Input value={mvForm.paymentMethod||""} onChange={e=>setMvForm(f=>({...f,paymentMethod:e.target.value}))}/></Field>
                    <Field label="Novo comprador rápido" col="1/-1">
                      <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:8}}>
                        <Input placeholder="Nome do comprador" value={mvForm.createBuyer?.name||""} onChange={e=>setMvForm(f=>({...f,createBuyer:{...(f.createBuyer||{}),name:e.target.value}}))}/>
                        <Input placeholder="CPF/CNPJ" value={mvForm.createBuyer?.cpfCnpj||""} onChange={e=>setMvForm(f=>({...f,createBuyer:{...(f.createBuyer||{}),cpfCnpj:e.target.value}}))}/>
                        <Input placeholder="Telefone" value={mvForm.createBuyer?.phone||""} onChange={e=>setMvForm(f=>({...f,createBuyer:{...(f.createBuyer||{}),phone:e.target.value}}))}/>
                        <Input placeholder="Endereço" value={mvForm.createBuyer?.address||""} onChange={e=>setMvForm(f=>({...f,createBuyer:{...(f.createBuyer||{}),address:e.target.value}}))}/>
                      </div>
                    </Field>
                    <Field label="Justificativa se faltar NF/valor" col="1/-1"><Input value={mvForm.justificationMissing||""} onChange={e=>setMvForm(f=>({...f,justificationMissing:e.target.value}))}/></Field>
                  </>}
                  {mvForm.type==="entrada" && mvForm.reason==="Compra" && <>
                    <Field label="Valor da compra (R$)"><Input type="number" min="0" step="0.01" value={mvForm.amount||""} onChange={e=>setMvForm(f=>({...f,amount:e.target.value}))}/></Field>
                    <Field label="Nota fiscal"><Input value={mvForm.nfNum||""} onChange={e=>setMvForm(f=>({...f,nfNum:e.target.value}))}/></Field>
                    <Field label="Vendedor"><Input value={mvForm.sellerName||""} onChange={e=>setMvForm(f=>({...f,sellerName:e.target.value}))}/></Field>
                    <Field label="Forma de pagamento"><Input value={mvForm.paymentMethod||""} onChange={e=>setMvForm(f=>({...f,paymentMethod:e.target.value}))}/></Field>
                    <Field label="Justificativa se faltar NF/valor" col="1/-1"><Input value={mvForm.justificationMissing||""} onChange={e=>setMvForm(f=>({...f,justificationMissing:e.target.value}))}/></Field>
                  </>}
                  <Field label="Observação" col="1/-1">
                    <Input value={mvForm.note} placeholder="Ex: Compra do Sr. José, Feira de Uberaba..."
                      onChange={e=>setMvForm(f=>({...f,note:e.target.value}))}/>
                  </Field>
                </div>
                <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                  <Btn size="sm" variant="ghost" onClick={()=>setMvForm(null)}>Cancelar</Btn>
                  <Btn size="sm" onClick={()=>{
                    if(mvForm.type!=="pesagem"&&!mvForm.qty){show("Informe a quantidade.","error");return;}
                    if((mvForm.type==="saída" || mvForm.type==="entrada") && !(mvForm.reason||"").trim()){ show("Selecione o motivo.","error"); return; }
                    if(mvForm.type==="saída" && mvForm.reason==="Venda externa" && !Number(mvForm.amount||0) && !(mvForm.justificationMissing||"").trim()){ show("Informe o valor da venda ou justifique a ausência.","error"); return; }
                    if(mvForm.type==="entrada" && mvForm.reason==="Compra" && !Number(mvForm.amount||0) && !(mvForm.justificationMissing||"").trim()){ show("Informe o valor da compra ou justifique a ausência.","error"); return; }
                    if(mvForm.type==="pesagem" && mvForm.weighMode==="individual"){
                      const entries=(mvForm.individualWeights||[]).map(v=>Number(v)).filter(v=>Number.isFinite(v)&&v>0);
                      if(!entries.length){show("Informe pelo menos um peso individual.","error");return;}
                      const avg=entries.reduce((s,v)=>s+v,0)/entries.length;
                      const yieldPct=Math.min(60, Math.max(45, Number(mvForm.carcassYieldPct||lot.carcassYieldPct||50)));
                      const carcassKg=avg*(yieldPct/100);
                      const note=[mvForm.note, `Pesagem individual sem identificação (${entries.length} animal(is)).`, `Média da balança: ${fmtMeasure(avg,2)} kg · rendimento ${fmtMeasure(yieldPct,1)}% · peso carcaça: ${fmtMeasure(carcassKg,2)} kg · ${fmtMeasure(carcassKg/15,2)} @.`].filter(Boolean).join(" ");
                      onAddMovement(lot,mvForm.type,entries.length,avg,{ note, carcassYieldPct:yieldPct });
                      setMvForm(null);
                      return;
                    }
                    onAddMovement(lot,mvForm.type,mvForm.qty||lot.quantity,mvForm.weight,{...mvForm, carcassYieldPct: mvForm.type==="pesagem" ? Math.min(60, Math.max(45, Number(mvForm.carcassYieldPct||lot.carcassYieldPct||50))) : undefined});
                    setMvForm(null);
                  }}>Registrar</Btn>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Movement history */}
        {(lot.movements||[]).length>0&&(
          <div>
            <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.8,marginBottom:10}}>
              📜 Histórico de Movimentações
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:0}}>
              {[...(lot.movements||[])].reverse().map((mv,i)=>{
                const mvCfg = {
                  "formação":    {icon:"📦",color:T.accent},
                  "entrada":     {icon:"➕",color:T.green},
                  "saída":       {icon:"➖",color:T.red},
                  "pesagem":     {icon:"⚖️",color:T.yellow},
                  "transferência":{icon:"🔄",color:T.purple},
                }[mv.type]||{icon:"📋",color:T.muted};
                return(
                  <div key={i} style={{display:"flex",gap:12,paddingBottom:10}}>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
                      <div style={{width:10,height:10,borderRadius:"50%",background:mvCfg.color,flexShrink:0,marginTop:4}}/>
                      {i<(lot.movements||[]).length-1&&<div style={{width:2,flex:1,background:`${mvCfg.color}33`,marginTop:2}}/>}
                    </div>
                    <div style={{flex:1,paddingBottom:4}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:16}}>{mvCfg.icon}</span>
                        <span style={{fontWeight:600,color:T.text,fontSize:13,textTransform:"capitalize"}}>{mv.type}</span>
                        {mv.qty>0&&<Badge color={mvCfg.color}>{mv.type==="pesagem"?"":mv.qty+" animais"}</Badge>}
                        {mv.weight>0&&<Badge color={T.yellow}>{fmtMeasure((Number(mv.weight||0) * (Number(mv.carcassYieldPct||lot.carcassYieldPct||50)/100))/15,2)} @/cab</Badge>}
                      </div>
                      {mv.note&&<div style={{fontSize:12,color:T.muted,marginTop:2}}>{mv.note}</div>}
                      {mv.weight>0&&<div style={{fontSize:11,color:T.muted,marginTop:2}}>Balança: {fmtMeasure(Number(mv.weight||0),1)} kg/cab · aproveitamento: {fmtMeasure(Number(mv.carcassYieldPct||lot.carcassYieldPct||50),1)}%</div>}
                      <div style={{fontSize:10,color:T.muted,marginTop:4}}>
                        {mv.by} · {new Date(mv.ts).toLocaleString("pt-BR")}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );

  return inline ? (
    <InlineModal theme={T} tone="warning" open={true} onClose={onClose} title="Detalhe do lote" wide>
      {content}
    </InlineModal>
  ) : (
    <Modal open={true} onClose={onClose} title="" wide>
      {content}
    </Modal>
  );
}




  return LotDetailModal;
}
